import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { useSyncLanguage } from "@/hooks/useLanguage";
import JsonLd, { articleJsonLd } from "@/components/feature/JsonLd";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { MAIN_SITE_URL } from "@/mocks/site";

export default function Article() {
  const { lang: langParam } = useParams();
  const lang = useSyncLanguage(langParam);
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [lang]);

  const tocItems = [
    t("s1.title"),
    t("s2.title"),
    t("s3.title"),
    t("s4.title"),
    t("s5.title"),
  ];

  const categories = ["shoes", "hoodies", "tshirts", "jackets", "pants", "headwear", "sets", "underwear", "jersey", "accessories", "bags", "belts", "perfumes", "other"];
  const checkList = [1, 2, 3, 4, 5];
  const faqList = [1, 2, 3, 4];

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd
        data={articleJsonLd({
          lang,
          slug: "usfans-spreadsheet-guide",
          title: t("title"),
          description: t("meta.description"),
        })}
      />
      <SiteHeader lang={lang} />

      <main>
        <article className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-14">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-sm text-foreground-500">
            <Link to={`/${lang}`} className="transition-colors hover:text-primary-700">
              {t("breadcrumb.home")}
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400" />
            <span className="text-foreground-700">{t("breadcrumb.current")}</span>
          </nav>

          {/* Title */}
          <h1 className="font-heading text-3xl font-extrabold leading-tight text-foreground-950 md:text-4xl">
            {t("title")}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-foreground-600 md:text-lg">{t("lead")}</p>

          <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-foreground-400">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" /> {new Date().toISOString().slice(0, 10)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-refresh-line" /> {new Date().toISOString().slice(0, 10)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-global-line" /> {t("brand.subtag")}
            </span>
          </div>

          {/* TOC */}
          <nav className="mt-8 rounded-lg border border-background-200 bg-background-100/60 p-5">
            <h2 className="font-heading text-sm font-semibold text-foreground-900">{t("toc.title")}</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {tocItems.map((item, i) => (
                <li key={i}>
                  <a href={`#section-${i + 1}`} className="flex items-center gap-2 text-foreground-600 transition-colors hover:text-primary-700">
                    <span className="text-primary-500">{i + 1}.</span>
                    <span>{item}</span>
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Section 1 */}
          <section id="section-1" className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-900 md:text-2xl">{t("s1.title")}</h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("s1.p1")}</p>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("s1.p2")}</p>
          </section>

          {/* Section 2 */}
          <section id="section-2" className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-900 md:text-2xl">{t("s2.title")}</h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("s2.p1")}</p>
            <ol className="mt-4 space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <li key={i} className="flex items-start gap-3 text-base leading-relaxed text-foreground-700">
                  <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">
                    {i}
                  </span>
                  {t(`s2.li${i}`)}
                </li>
              ))}
            </ol>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("s2.p2")}</p>
          </section>

          {/* Section 3 - categories with internal links back to home */}
          <section id="section-3" className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-900 md:text-2xl">{t("s3.title")}</h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("s3.p1")}</p>
            <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {categories.map((cat) => (
                <li key={cat}>
                  <Link
                    to={`/${lang}`}
                    className="flex items-start gap-2 rounded-md px-2 py-1.5 text-sm text-foreground-600 transition-colors hover:bg-background-100 hover:text-primary-700"
                  >
                    <i className="ri-price-tag-3-line mt-0.5 text-foreground-400" />
                    {t(`s3.${cat}`)}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("s3.p2")}</p>
          </section>

          {/* Section 4 */}
          <section id="section-4" className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-900 md:text-2xl">{t("s4.title")}</h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("s4.p1")}</p>
            <ul className="mt-4 space-y-2">
              {checkList.map((i) => (
                <li key={i} className="flex items-start gap-3 text-base leading-relaxed text-foreground-700">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" />
                  {t(`s4.li${i}`)}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("s4.p2")}</p>
          </section>

          {/* Section 5 */}
          <section id="section-5" className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-900 md:text-2xl">{t("s5.title")}</h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("s5.p1")}</p>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("s5.p2")}</p>
          </section>

          {/* Comparison table */}
          <section className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-900 md:text-2xl">{t("table.caption")}</h2>
            <div className="mt-5 overflow-x-auto rounded-lg border border-background-200">
              <table className="w-full min-w-[560px] border-collapse text-sm">
                <thead>
                  <tr className="bg-background-100 text-left text-foreground-900">
                    <th className="px-4 py-3 font-heading font-semibold">{t("table.head1")}</th>
                    <th className="px-4 py-3 font-heading font-semibold">{t("table.head2")}</th>
                    <th className="px-4 py-3 font-heading font-semibold">{t("table.head3")}</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4].map((r) => (
                    <tr key={r} className="border-t border-background-200 text-foreground-700">
                      <td className="px-4 py-3 font-medium text-foreground-900">{t(`table.r${r}c1`)}</td>
                      <td className="px-4 py-3">{t(`table.r${r}c2`)}</td>
                      <td className="px-4 py-3">{t(`table.r${r}c3`)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* FAQ */}
          <section className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-900 md:text-2xl">{t("faq.title")}</h2>
            <div className="mt-5 space-y-4">
              {faqList.map((q) => (
                <details key={q} className="group rounded-lg border border-background-200 bg-background-50 p-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-heading text-base font-semibold text-foreground-900">
                    {t(`faq.q${q}`)}
                    <i className="ri-add-line text-lg text-foreground-500 transition-transform group-open:rotate-45" />
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">{t(`faq.a${q}`)}</p>
                </details>
              ))}
            </div>
          </section>

          {/* Safety note */}
          <section className="mt-10 rounded-lg border border-accent-200 bg-accent-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-accent-900">
              <i className="ri-shield-check-line" /> {t("safety.title")}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-accent-800">{t("safety.text")}</p>
          </section>

          {/* CTA with internal link back to home */}
          <section className="mt-10 rounded-lg bg-primary-500 px-6 py-10 text-center">
            <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">{t("cta.title")}</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">{t("cta.text")}</p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to={`/${lang}`}
                className="whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100"
              >
                <i className="ri-home-line mr-1.5" /> {t("cta.back")}
              </Link>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                {t("cta.spreadsheet")}
              </a>
            </div>
          </section>
        </article>
      </main>

      <SiteFooter lang={lang} />
    </div>
  );
}